import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  MapPin,
  Edit,
  ArrowUpDown,
  Search,
  Filter,
  Plus,
  Calendar,
  Hash,
  Building,
  X,
  XCircle as XCircleIcon,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import api from "../../../services/axiosInstance";
import { Location } from "../../../types/location";
import Select from "../../../shared/components/form/Select";
import { safeFormatDate, safeFormatDistance } from "../../../utils/dateUtils";
import { BRAND_COLORS } from "../../../shared/constants/brandColors";

type OptionType = { label: string; value: string };

// ToggleStatusCell with right-side drawer confirmation (same pattern as ProjectTypeTable)
interface ToggleStatusCellProps {
  location: Location;
  onRefresh: () => void;
}

const ToggleStatusCell: React.FC<ToggleStatusCellProps> = ({ location, onRefresh }) => {
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const rawId = location._id || location.id;
  const locationId = rawId ? String(rawId) : "";
  const isActive = location.status === "active";

  const handleToggle = async () => {
    if (!locationId) {
      toast.error("Location ID is missing");
      return;
    }
    setIsLoading(true);
    try {
      const endpoint = isActive ? "inactivate" : "activate";
      await api.patch(`/locations/${locationId}/${endpoint}`);
      toast.success(`Location ${isActive ? "deactivated" : "activated"} successfully`);
      setDrawerOpen(false);
      onRefresh();
    } catch (err: unknown) {
      console.error("Toggle error:", err);
      toast.error("Failed to toggle location status");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        title={`Click to ${isActive ? "Deactivate" : "Activate"}`}
        disabled={!locationId}
        className={`relative inline-flex items-center justify-center w-10 h-5 rounded-full border transition-all duration-300 select-none disabled:opacity-60 ${isActive
          ? "bg-green-50 border-green-200 hover:bg-green-100"
          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
          }`}
      >
        <div
          className={`w-3 h-3 rounded-full transform transition-all duration-300 ${isActive
            ? "translate-x-2 bg-green-500"
            : "-translate-x-2 bg-gray-400"
            }`}
        />
      </button>

      <AnimatePresence>
        {isDrawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[100] bg-black/30 backdrop-blur-sm"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            {/* Right drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
              className="fixed top-0 right-0 z-[101] h-full w-full max-w-md sm:max-w-lg bg-white border-l border-gray-200 shadow-2xl flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center border flex-shrink-0 ${isActive ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                    {isActive ? (
                      <XCircleIcon size={24} className="text-red-500" />
                    ) : (
                      <CheckCircle size={24} className="text-green-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">
                      {isActive ? "Deactivate Location" : "Activate Location"}
                    </h2>
                    <p className="text-sm text-gray-600 break-words">
                      {location.name}
                    </p>
                    {location.code && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Code: {location.code}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-all duration-200 shrink-0"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center border border-amber-200 flex-shrink-0">
                    <AlertTriangle size={20} className="text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base text-gray-700 mb-4 leading-relaxed">
                      Are you sure you want to{" "}
                      <span className="font-medium text-gray-900">
                        {isActive ? "deactivate" : "activate"}
                      </span>{" "}
                      <span className="font-medium break-words" style={{ color: BRAND_COLORS.primary }}>{location.name}</span>?
                    </p>
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {isActive
                          ? "This location will no longer be available for selection in projects and other modules."
                          : "This location will become available for selection in projects and other modules."
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 shrink-0">
                <button
                  onClick={() => setDrawerOpen(false)}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleToggle}
                  disabled={isLoading}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-lg transition-all duration-200 disabled:opacity-50 ${isActive
                    ? "bg-red-500 hover:bg-red-600 focus:ring-red-500"
                    : "bg-green-500 hover:bg-green-600 focus:ring-green-500"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>
                      {isActive ? "Deactivate" : "Activate"}
                    </span>
                  )}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

interface LocationFilterProps {
  status: string | null;
  setStatus: (val: string | null) => void;
}

const LocationFilter = ({ status, setStatus }: LocationFilterProps) => {
  const { control } = useForm({});

  const statusOptions: OptionType[] = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const hasActiveFilters = status && status !== "all";

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
      <div className="flex-1 min-w-[180px]">
        <Controller
          name="status"
          control={control}
          render={() => (
            <Select
              options={statusOptions}
              placeholder="Filter by Status"
              value={statusOptions.find((opt) => opt.value === (status || "all"))}
              onChange={(selectedOption: OptionType | null) => {
                setStatus(selectedOption?.value || "all");
              }}
            />
          )}
        />
      </div>

      {hasActiveFilters && (
        <button
          type="button"
          onClick={() => setStatus("all")}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 hover:text-gray-700 hover:border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ "--tw-ring-color": BRAND_COLORS.primary } as React.CSSProperties}
        >
          <X size={14} />
          Clear
        </button>
      )}
    </div>
  );
};

interface LocationTableProps {
  locations: Location[];
  search: string;
  setSearch: (val: string) => void;
  status: string | null;
  setStatus: (val: string | null) => void;
  page: number;
  setPage: (val: number) => void;
  totalLocations: number;
  onEdit?: (location: Location) => void;
  onAdd?: () => void;
  onRefresh: () => void;
  sortField: string;
  sortOrder: "asc" | "desc";
  setSortField: (field: string) => void;
  setSortOrder: (order: "asc" | "desc") => void;
  loading?: boolean;
}

const ITEMS_PER_PAGE = 6;

export default function LocationTableNew({
  locations,
  search,
  setSearch,
  status,
  setStatus,
  page,
  setPage,
  totalLocations,
  onEdit,
  onAdd,
  onRefresh,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  loading = false,
}: LocationTableProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [sortMenuOpen, setSortMenuOpen] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const totalPages = Math.ceil(totalLocations / ITEMS_PER_PAGE);

  const handleSortOption = (field: string, order: "asc" | "desc" | "clear") => {
    if (order === "clear") {
      setSortField("createdAt");
      setSortOrder("desc");
    } else {
      setSortField(field);
      setSortOrder(order);
    }
    setPage(1);
    setSortMenuOpen(null);
  };

  const handleReset = () => {
    setSearch("");
    setStatus("all");
    setSortField("createdAt");
    setSortOrder("desc");
    setPage(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
    >
      <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-white to-gray-25">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search locations... (Ctrl+K)"
                className="block w-full sm:w-80 pl-9 pr-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 bg-white"
                style={{ "--tw-ring-color": BRAND_COLORS.primary } as React.CSSProperties}
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 border ${showFilters
                ? "text-white border-transparent"
                : "text-gray-600 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
              style={showFilters ? {
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
              } : {}}
            >
              <Filter size={16} />
              Filters
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setSortMenuOpen(sortMenuOpen ? null : "general")}
                className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 transition-all duration-200"
                style={{ "--tw-ring-color": BRAND_COLORS.primary } as React.CSSProperties}
              >
                <ArrowUpDown size={16} />
                Sort
              </button>

              <AnimatePresence>
                {sortMenuOpen === "general" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-2 z-20 w-48 bg-white border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div className="p-3">
                      <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Name</div>
                      <button
                        onClick={() => handleSortOption("name", "asc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "name" && sortOrder === "asc"
                          ? "font-medium bg-opacity-10"
                          : "text-gray-700"
                          }`}
                        style={sortField === "name" && sortOrder === "asc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        A → Z
                      </button>
                      <button
                        onClick={() => handleSortOption("name", "desc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "name" && sortOrder === "desc"
                          ? "font-medium bg-opacity-10"
                          : "text-gray-700"
                          }`}
                        style={sortField === "name" && sortOrder === "desc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Z → A
                      </button>

                      <div className="border-t border-gray-200 my-2"></div>
                      <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Date</div>
                      <button
                        onClick={() => handleSortOption("createdAt", "desc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "createdAt" && sortOrder === "desc"
                          ? "font-medium bg-opacity-10"
                          : "text-gray-700"
                          }`}
                        style={sortField === "createdAt" && sortOrder === "desc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Newest First
                      </button>
                      <button
                        onClick={() => handleSortOption("createdAt", "asc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "createdAt" && sortOrder === "asc"
                          ? "font-medium bg-opacity-10"
                          : "text-gray-700"
                          }`}
                        style={sortField === "createdAt" && sortOrder === "asc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Oldest First
                      </button>
                      <button
                        onClick={() => handleSortOption("createdAt", "clear")}
                        className="w-full mt-2 flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 text-gray-700"
                      >
                        Reset Sort
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {onAdd && (
              <button
                onClick={onAdd}
                className="inline-flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-xl shadow-lg shadow-emerald-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-emerald-200"
                style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                  "--tw-ring-color": BRAND_COLORS.primary
                } as React.CSSProperties}
              >
                <div className="relative">
                  <Plus size={16} className="absolute animate-ping opacity-40" />
                  <Plus size={16} className="relative" />
                </div>
                <span>Add Location</span>
              </button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-4"
            >
              <LocationFilter
                status={status}
                setStatus={(val) => {
                  setStatus(val || "all");
                  setPage(1);
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BRAND_COLORS.primary }}></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                {[
                  { label: "Code", icon: <Hash size={14} /> },
                  { label: "Location", icon: <MapPin size={14} /> },
                  { label: "Type", icon: <Building size={14} /> },
                  { label: "Created", icon: <Calendar size={14} /> },
                  { label: "Status", icon: <Filter size={14} /> },
                ].map(({ label, icon }) => (
                  <th key={label} className="p-4 text-left text-sm font-medium text-gray-700 tracking-wide">
                    <div className="flex items-center gap-2">
                      {icon}
                      <span>{label}</span>
                    </div>
                  </th>
                ))}
                <th className="p-4 text-center text-sm font-medium text-gray-700 tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              <AnimatePresence>
                {locations.map((location, index) => {
                  const rawId = location._id || location.id;
                  const locationId = rawId ? String(rawId) : "";
                  const isActive = location.status === "active";

                  return (
                    <motion.tr
                      key={locationId || `row-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className={`transition-all duration-200 ${!isActive
                        ? "bg-gray-50/50 opacity-60 hover:bg-gray-100/60"
                        : "hover:bg-gradient-to-r hover:from-gray-25 hover:to-gray-50"
                        }`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Hash size={14} className={!isActive ? "text-gray-300" : "text-gray-400"} />
                          <span className={`text-sm font-medium ${!isActive ? "text-gray-500" : "text-gray-900"}`}>
                            {location.code || "N/A"}
                          </span>
                        </div>
                      </td>

                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className={!isActive ? "text-gray-300" : "text-gray-400"} />
                          <div>
                            <div className={`text-sm font-medium ${!isActive ? "text-gray-500" : "text-gray-900"}`}>
                              {location.name}
                            </div>
                            <div className={`text-xs ${!isActive ? "text-gray-400" : "text-gray-500"}`}>
                              {location.city || location.mainAddress?.city || "No city"},{" "}
                              {location.state || location.mainAddress?.state || "No state"}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="p-4">
                        <span className={`text-sm ${!isActive ? "text-gray-500" : "text-gray-900"}`}>
                          {location.type || "N/A"}
                        </span>
                      </td>

                      <td className="p-4">
                        <div className="flex flex-col">
                          {location.createdAt ? (
                            <>
                              <span className={`text-sm ${!isActive ? "text-gray-500" : "text-gray-900"}`}>
                                {safeFormatDate(location.createdAt, "MMM dd, yyyy")}
                              </span>
                              <span className={`text-xs ${!isActive ? "text-gray-400" : "text-gray-500"}`}>
                                {safeFormatDistance(location.createdAt, { addSuffix: true })}
                              </span>
                            </>
                          ) : (
                            <span className={`text-sm ${!isActive ? "text-gray-500" : "text-gray-900"}`}>N/A</span>
                          )}
                        </div>
                      </td>

                      <td className="p-4">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${isActive
                            ? "text-green-700 bg-green-50 border-green-200"
                            : "text-gray-600 bg-gray-50 border-gray-200"
                            }`}
                        >
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="p-4">
                        <div className="flex items-center justify-center gap-3">
                          <ToggleStatusCell location={location} onRefresh={onRefresh} />

                          {onEdit && (
                            <button
                              onClick={() => onEdit(location)}
                              title="Edit Location"
                              className={`transition-colors duration-200 ${!isActive
                                ? "text-gray-300 hover:text-gray-400"
                                : "text-gray-400 hover:text-emerald-400"
                                }`}
                            >
                              <Edit size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}

      {!loading && locations.length === 0 && (
        <div className="p-16 text-center">
          <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6 border border-gray-200"
            style={{ background: `linear-gradient(135deg, ${BRAND_COLORS.primary}20, ${BRAND_COLORS.primary}40)` }}
          >
            <MapPin size={24} className="text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">No locations found</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
            {search || (status && status !== "all")
              ? "Try adjusting your search or filter criteria to find the locations you're looking for"
              : "Get started by adding your first location to the system"}
          </p>
          {!search && (!status || status === "all") && onAdd && (
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                "--tw-ring-color": BRAND_COLORS.primary
              } as React.CSSProperties}
            >
              <Plus size={16} />
              Add First Location
            </button>
          )}
        </div>
      )}

      {!loading && locations.length > 0 && (
        <div className="px-5 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-25 to-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-600 font-medium">
              Showing <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min((page - 1) * ITEMS_PER_PAGE + 1, totalLocations)}</span> to{" "}
              <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min(page * ITEMS_PER_PAGE, totalLocations)}</span> of{" "}
              <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{totalLocations}</span> locations
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(page - 1, 1))}
                disabled={page === 1}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                style={{ "--tw-ring-color": BRAND_COLORS.primary } as React.CSSProperties}
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-md transition-all duration-200 ${page === pageNum
                        ? "text-white border border-transparent"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                        }`}
                      style={page === pageNum ? {
                        background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
                      } : {}}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setPage(Math.min(page + 1, totalPages))}
                disabled={page === totalPages}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                style={{ "--tw-ring-color": BRAND_COLORS.primary } as React.CSSProperties}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
